/**
 * Owner: Masa Bayu
 * File: layout.ts
 * Date:Time: 2026-09-27 10:42
 * Description: Page · Line · InlineRun · layout blocks — Eyes evidence (≠ chapter semantics).
 */

import { createQXDocumentConfidence, type QXDocumentConfidence } from './confidence';
import {
  createQXDocumentBBox,
  createQXDocumentTextStyle,
  type QXDocumentBBox,
  type QXDocumentTextStyle,
} from './geometry';
import { assertOptionalTrimmed, assertValidId, isRecord } from './guards';
import { createQXDocumentId } from './id';
import {
  createQXDocumentProvenance,
  type CreateQXDocumentProvenanceOptions,
  type QXDocumentProvenance,
} from './provenance';

export const QX_DOCUMENT_LAYOUT_BLOCK_KINDS = [
  'text',
  'image',
  'table',
  'note',
  'header',
  'footer',
  'page_number',
  'caption',
  'other',
] as const;
export type QXDocumentLayoutBlockKind = (typeof QX_DOCUMENT_LAYOUT_BLOCK_KINDS)[number];

export interface QXDocumentInlineRun {
  readonly runId: string;
  readonly text: string;
  readonly bbox: QXDocumentBBox | null;
  readonly style: QXDocumentTextStyle;
}

/** Optional L2 line-paint carry · Eyes evidence · R11-LINE-01 or R11-LINE-02. */
export interface QXDocumentLinePaint {
  readonly extension?: 'R11-LINE-01' | 'R11-LINE-02';
  readonly wordStarts?: readonly number[];
  readonly wordGaps?: readonly number[];
  readonly segments?: readonly {
    readonly text: string;
    readonly x: number;
    readonly width: number;
  }[];
  readonly direction?: 'ltr' | 'rtl' | 'ttb';
  readonly rotation?: number;
  readonly column?: number;
  readonly script?: 'latin' | 'arabic' | 'cjk' | 'mixed' | 'other';
  readonly role?: string;
}

export interface QXDocumentLine {
  readonly lineId: string;
  readonly bbox: QXDocumentBBox | null;
  readonly runs: readonly QXDocumentInlineRun[];
  readonly linePaint?: QXDocumentLinePaint;
}

export interface QXDocumentLayoutBlock {
  readonly layoutBlockId: string;
  readonly kind: QXDocumentLayoutBlockKind;
  readonly bbox: QXDocumentBBox | null;
  readonly lines: readonly QXDocumentLine[];
  readonly text: string | null;
  readonly assetId: string | null;
  readonly confidence: QXDocumentConfidence | null;
  readonly provenance: QXDocumentProvenance;
}

export interface QXDocumentPage {
  readonly pageId: string;
  readonly pageNumber: number;
  readonly width: number;
  readonly height: number;
  readonly blocks: readonly QXDocumentLayoutBlock[];
}

export interface QXDocumentLayout {
  readonly pages: readonly QXDocumentPage[];
}

function assertKind(value: unknown): QXDocumentLayoutBlockKind {
  if (
    typeof value !== 'string' ||
    !(QX_DOCUMENT_LAYOUT_BLOCK_KINDS as readonly string[]).includes(value)
  ) {
    throw new Error('layout block kind is unknown');
  }
  return value as QXDocumentLayoutBlockKind;
}

function assertPositivePage(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    throw new Error('page.pageNumber must be a positive integer');
  }
  return value;
}

function assertFinite(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${label} must be a finite number`);
  }
  return value;
}

function optionalBbox(value: unknown): QXDocumentBBox | null {
  if (value === undefined || value === null) return null;
  return createQXDocumentBBox(value as QXDocumentBBox);
}

export function createQXDocumentInlineRun(options: {
  readonly runId?: string;
  readonly text: string;
  readonly bbox?: QXDocumentBBox | null;
  readonly style?: QXDocumentTextStyle | Parameters<typeof createQXDocumentTextStyle>[0];
}): QXDocumentInlineRun {
  if (!isRecord(options)) throw new Error('inline run must be an object');
  const runId = options.runId ?? createQXDocumentId('irrun');
  assertValidId(runId, 'run.runId');
  const text = typeof options.text === 'string' ? options.text : '';
  return Object.freeze({
    runId,
    text,
    bbox: optionalBbox(options.bbox),
    style: createQXDocumentTextStyle(options.style),
  });
}

function optionalLinePaint(value: unknown): QXDocumentLinePaint | undefined {
  if (value === undefined || value === null) return undefined;
  if (!isRecord(value)) throw new Error('line.linePaint must be an object');
  const out: {
    -readonly [K in keyof QXDocumentLinePaint]?: QXDocumentLinePaint[K];
  } = {};
  if ('extension' in value) {
    const e = value.extension;
    if (e !== 'R11-LINE-01' && e !== 'R11-LINE-02') {
      throw new Error('line.linePaint.extension must be R11-LINE-01|R11-LINE-02');
    }
    out.extension = e;
  }
  if ('segments' in value) {
    if (!Array.isArray(value.segments)) {
      throw new Error('line.linePaint.segments must be an array');
    }
    const segs: NonNullable<QXDocumentLinePaint['segments']>[number][] = [];
    for (const seg of value.segments) {
      if (!isRecord(seg)) throw new Error('line.linePaint.segments item must be object');
      if (typeof seg.text !== 'string' || seg.text.length === 0) {
        throw new Error('line.linePaint.segments[].text must be non-empty string');
      }
      const x = assertFinite(seg.x, 'line.linePaint.segments[].x');
      const width = assertFinite(seg.width, 'line.linePaint.segments[].width');
      if (width < 0) throw new Error('line.linePaint.segments[].width must be ≥ 0');
      segs.push(Object.freeze({ text: seg.text, x, width }));
    }
    out.segments = Object.freeze(segs);
  }
  if ('wordStarts' in value) {
    if (!Array.isArray(value.wordStarts) || !value.wordStarts.every((n) => typeof n === 'number' && Number.isFinite(n))) {
      throw new Error('line.linePaint.wordStarts must be finite numbers');
    }
    out.wordStarts = Object.freeze([...value.wordStarts] as number[]);
  }
  if ('wordGaps' in value) {
    if (!Array.isArray(value.wordGaps) || !value.wordGaps.every((n) => typeof n === 'number' && Number.isFinite(n))) {
      throw new Error('line.linePaint.wordGaps must be finite numbers');
    }
    out.wordGaps = Object.freeze([...value.wordGaps] as number[]);
  }
  if ('direction' in value) {
    const d = value.direction;
    if (d !== 'ltr' && d !== 'rtl' && d !== 'ttb') {
      throw new Error('line.linePaint.direction must be ltr|rtl|ttb');
    }
    out.direction = d;
  }
  if ('rotation' in value) {
    out.rotation = assertFinite(value.rotation, 'line.linePaint.rotation');
  }
  if ('column' in value) {
    const c = value.column;
    if (typeof c !== 'number' || !Number.isInteger(c) || c < 0) {
      throw new Error('line.linePaint.column must be a non-negative integer');
    }
    out.column = c;
  }
  if ('script' in value) {
    const s = value.script;
    if (
      s !== 'latin' &&
      s !== 'arabic' &&
      s !== 'cjk' &&
      s !== 'mixed' &&
      s !== 'other'
    ) {
      throw new Error('line.linePaint.script must be latin|arabic|cjk|mixed|other');
    }
    out.script = s;
  }
  if ('role' in value) {
    if (typeof value.role !== 'string' || value.role.trim().length === 0) {
      throw new Error('line.linePaint.role must be a non-empty string');
    }
    out.role = value.role;
  }
  return Object.freeze(out);
}

export function createQXDocumentLine(options: {
  readonly lineId?: string;
  readonly bbox?: QXDocumentBBox | null;
  readonly runs?: readonly (QXDocumentInlineRun | Parameters<typeof createQXDocumentInlineRun>[0])[];
  readonly linePaint?: QXDocumentLinePaint | null;
}): QXDocumentLine {
  if (!isRecord(options)) throw new Error('line must be an object');
  const lineId = options.lineId ?? createQXDocumentId('irline');
  assertValidId(lineId, 'line.lineId');
  const linePaint = optionalLinePaint(options.linePaint);
  return Object.freeze({
    lineId,
    bbox: optionalBbox(options.bbox),
    runs: Object.freeze((options.runs ?? []).map((run) => createQXDocumentInlineRun(run))),
    ...(linePaint !== undefined ? { linePaint } : {}),
  });
}

export function createQXDocumentLayoutBlock(options: {
  readonly layoutBlockId?: string;
  readonly kind: QXDocumentLayoutBlockKind;
  readonly bbox?: QXDocumentBBox | null;
  readonly lines?: readonly (QXDocumentLine | Parameters<typeof createQXDocumentLine>[0])[];
  readonly text?: string | null;
  readonly assetId?: string | null;
  readonly confidence?: QXDocumentConfidence | null | Parameters<typeof createQXDocumentConfidence>[0];
  readonly provenance: CreateQXDocumentProvenanceOptions | QXDocumentProvenance;
}): QXDocumentLayoutBlock {
  if (!isRecord(options)) throw new Error('layout block must be an object');
  const layoutBlockId = options.layoutBlockId ?? createQXDocumentId('irlblk');
  assertValidId(layoutBlockId, 'layoutBlock.layoutBlockId');
  const confidence =
    options.confidence === undefined || options.confidence === null
      ? null
      : createQXDocumentConfidence(options.confidence);
  return Object.freeze({
    layoutBlockId,
    kind: assertKind(options.kind),
    bbox: optionalBbox(options.bbox),
    lines: Object.freeze((options.lines ?? []).map((line) => createQXDocumentLine(line))),
    text: options.text === undefined ? null : assertOptionalTrimmed(options.text, 'layoutBlock.text'),
    assetId: assertOptionalTrimmed(options.assetId, 'layoutBlock.assetId'),
    confidence,
    provenance: createQXDocumentProvenance(options.provenance),
  });
}

export function createQXDocumentPage(options: {
  readonly pageId?: string;
  readonly pageNumber: number;
  readonly width: number;
  readonly height: number;
  readonly blocks?: readonly (
    | QXDocumentLayoutBlock
    | Parameters<typeof createQXDocumentLayoutBlock>[0]
  )[];
}): QXDocumentPage {
  if (!isRecord(options)) throw new Error('page must be an object');
  const pageId = options.pageId ?? createQXDocumentId('irpage');
  assertValidId(pageId, 'page.pageId');
  return Object.freeze({
    pageId,
    pageNumber: assertPositivePage(options.pageNumber),
    width: assertFinite(options.width, 'page.width'),
    height: assertFinite(options.height, 'page.height'),
    blocks: Object.freeze(
      (options.blocks ?? []).map((block) => createQXDocumentLayoutBlock(block)),
    ),
  });
}

export function createQXDocumentLayout(options?: {
  readonly pages?: readonly (QXDocumentPage | Parameters<typeof createQXDocumentPage>[0])[];
}): QXDocumentLayout {
  return Object.freeze({
    pages: Object.freeze((options?.pages ?? []).map((page) => createQXDocumentPage(page))),
  });
}
